import { autoinject, bindable, Container } from 'aurelia-framework';
import { ListFieldTyped } from 'models/ListSettings';
import {
  CommentForEntity,
  MooringAnchorLine,
  MooringArticleSearchResult,
  MooringComponent,
  MooringComponentType,
  MooringOfferDto,
} from 'models/mooring';
import { MooringComponentSearchHandler, MooringOfferService, MooringService } from 'services/mooring';
import { MooringArticleService, ToastService } from 'services';
import { toKg, toM, toChecked } from 'lib/ui/ValueDisplayFormatters';
import { DialogService } from 'aurelia-dialog';
import { TableEntryManager } from 'services/ui-helpers';
import { MooringTableBase } from '../table-base';
import { PubSub } from 'lib/event/PubSub';
import { I18N } from 'aurelia-i18n';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { AsyncErrorHandler } from 'lib/ui';
import { TableManager } from '../TableManager';
import { cleanDmsFormat, withNumber } from 'lib/helpers';
import { calculateFactorsMblAnchoring } from '../factors';
import { SearchResult } from 'elements/search/search-input';
import { MooringSelectEntriesForPupulateArgs } from 'features/mooring/component-templates/mooring-select-entries-for-populate';
import { AnchorLineBuoySelectionDialog, AnchorLineBuoySelectionDialogArgs } from './anchor-line-buoy-selection-dialog';
import { MaterialFactor } from 'models/mooring/MaterialFactor';
import { MaterialFactorService } from 'services/material-factor-service';
import { CalculateUtilizationFactorDto, MooringStep } from 'models/mooring/UtilizationFactor';
import { OfferViewContext } from '../../OfferViewContext';
import { MooringComponentSearchResult } from 'services/mooring/mooring-component-search-handler';

@autoinject
export class AnchorLinesTable extends MooringTableBase<MooringAnchorLine> {
  protected table = new TableManager('anchor-lines-table', tableColumns);
  protected mooringOffer: MooringOfferDto;

  @bindable
  protected onSave: (args: { entries: MooringAnchorLine[]; canNavigateOnSave: boolean }) => void;

  protected context = OfferViewContext.useContext(this.container);

  constructor(
    private container: Container,
    protected t: I18N,
    protected toast: ToastService,
    dialogService: DialogService,
    mooringArticleService: MooringArticleService,
    // Used in HTML for article search
    mooringComponentSearchHandler: MooringComponentSearchHandler,
    tableEntryManager: TableEntryManager,
    pubsub: PubSub,
    confirmation: ConfirmDialog,
    mooringOfferService: MooringOfferService,
    mooringService: MooringService,
    materialFactorService: MaterialFactorService,
    materialFactors: MaterialFactor
  ) {
    super(
      'anchor-line',
      dialogService,
      mooringComponentSearchHandler,
      tableEntryManager,
      mooringArticleService,
      pubsub,
      confirmation,
      mooringOfferService,
      mooringService,
      materialFactorService,
      materialFactors
    );

    pubsub.sub('tabs:change-guard', async () => {
      if (this.changeTracker.areEqual(this.entries)) {
        this.pubsub.publish('tabs:change-guard-ok', null);
        return;
      }

      const res = await this.confirmation.confirmSave();
      if (res == 'cancel') {
        return;
      } else if (res === 'discard-continue') {
        this.pubsub.publish('tabs:change-guard-ok', null);
      } else {
        void this.onSave({ entries: this.entries, canNavigateOnSave: true });
      }
    });
  }

  protected onArticleSelected(searchResult: MooringComponentSearchResult, component: MooringComponent, line: MooringAnchorLine) {
    const res = searchResult.return;
    if (!res) {
      return;
    }

    component.ArticleProductId = res.Id;
    component.MBL = res.MBL;
    component.ProductName = res.ProductName;
    component.CategoryName = res.Category;
    component.TypeName = res.Type;
    component.ProductTypeName = res.ProductType;
    component.PrimaryDimension = res.PrimaryDimension;
    component.SecondaryDimension = res.SecondaryDimension;
    component.CategoryType = res.CategoryType;
    component.TypeVariant = res.TypeVariant;

    this.setComponentQuantity(component, line);

    this.onSave({
      entries: this.entries,
      canNavigateOnSave: false,
    });
  }

  async attached() {
    await super.attached();

    const { data: offer } = await this.mooringOfferService.getOffer(this.offerId);
    this.mooringOffer = offer;
  }

  protected onElementSelected(type: MooringComponentType, item: { id: number; title: string }, line: MooringAnchorLine) {
    if (type == 'buoy') {
      line.BuoyId = item.id;
      line.BuoyNo = item.title;
    }
  }

  protected getEntriesForPopulate(): MooringSelectEntriesForPupulateArgs {
    const entries = structuredClone(this.entries);
    return {
      groups: [
        {
          _identifier: 'anchorLines',
          title: this.t.tr('mooring.anchorLine_plural'),
          options: entries.map((x) => ({
            name: `${this.t.tr('mooring.anchorLineNo')} ${x.AnalysisNo || ''}\n${this.t.tr('mooring.buoy')}: ${x.BuoyNo}`,
            id: x.Id,
            selected: false,
            articles: x.Components?.length || 0,
          })),
        },
      ],
    };
  }

  protected onAddComponent() {
    this.entries.push({
      AnalysisNo: ``,
    } as MooringAnchorLine);
  }

  @AsyncErrorHandler
  protected async onDelete(index: number) {
    const res = await this.confirmation.confirmDelete();
    if (!res) return;

    const entry = this.entries[index];
    if (!entry) return;

    if (entry.Id) {
      await this.mooringService.deleteAnchorLine(this.mooringId, entry.Id);
    }

    this.entries.splice(index, 1);
    this.toast.showDeleted();
  }

  protected save() {
    this.onSave({
      canNavigateOnSave: false,
      entries: this.entries,
    });
  }

  protected async setBuoys() {
    await this.dialogService
      .open({
        viewModel: AnchorLineBuoySelectionDialog,
        position: () => {
          //
        },
        model: {
          mooringId: this.mooringId,
          lines: this.entries.map((line) => ({
            id: line.Id,
            name: `${line.AnalysisNo || ''}`,
            buoyId: line.BuoyId,
          })),
        },
      })
      .whenClosed((response) => {
        if (response.wasCancelled) return;

        const selectedLines = response.output as AnchorLineBuoySelectionDialogArgs['lines'];

        for (const line of this.entries) {
          const selectedLine = selectedLines.find((x) => x.id === line.Id);
          if (selectedLine) {
            line.BuoyId = selectedLine.buoyId || null;
          }
        }

        this.onSave({
          entries: this.entries,
          canNavigateOnSave: false,
        });
      });
  }

  protected addEntryComment(component: MooringAnchorLine) {
    this.openCommentDialog({
      mooringId: this.mooringId,
      isReadOnly: false,
      componentId: component.Id,
      componentType: CommentForEntity.AnchorLine,
    });
  }

  protected addComponentComment(component: MooringComponent) {
    this.openCommentDialog({
      mooringId: this.mooringId,
      isReadOnly: false,
      componentId: component.Id,
      componentType: CommentForEntity.AnchorLineComponent,
    });
  }

  protected async clearForm() {
    const approved = await this.confirmation.confirmYesCancel('mooring.clearForm', 'mooring.clearFormConfirmation');

    if (approved) {
      const restedTable = this.resetFields(this.entries, fieldsToReset);
      this.entries = restedTable;
    }
  }

  protected resetFields<T extends Record<string, any>>(anchorLines: T[], fieldsToReset: (keyof T)[]): T[] {
    return anchorLines.map((anchorLine) => {
      const updatedAnchorLine = { ...anchorLine };

      fieldsToReset.forEach((field) => {
        const value = updatedAnchorLine[field];

        if (field === 'LengthOfBottomRope' || field === 'LengthOfRope' || field === 'TotalLength') {
          (updatedAnchorLine[field] as number) = 0;
        } else if (typeof value === 'boolean') {
          (updatedAnchorLine[field] as boolean) = false;
        } else {
          updatedAnchorLine[field] = null;
        }
      });

      return updatedAnchorLine;
    });
  }

  protected onInputChange(index: number, name: (typeof tableColumns)[number]['field']) {
    const line = this.entries[index];

    if (name === 'LengthOfRope' || name === 'LengthOfBottomRope') {
      line.TotalLength = withNumber(line.LengthOfBottomRope).add(line.LengthOfRope).value;
      this.setComponentsQuantity(line.Components, line);
    }

    if (name === 'LengthOfRope' || name === 'LengthOfBottomRope' || name === 'DepthAtAnchoringPoint' || name === 'TotalLength') {
      const depth = withNumber(line.DepthAtAnchoringPoint).subtract(this.mooringOffer.SiteSetup.MooringDepth).value;
      line.Slope = withNumber(line.TotalLength).divideBy(depth).value;
    }

    if (name === 'StartPositionLat' || name === 'StartPositionLong' || name === 'StopPositionLat' || name === 'StopPositionLong') {
      line[name] = cleanDmsFormat(line[name]);
    }

    if (name === 'IsAccident' || name === 'DimForce') {
      calculateFactorsMblAnchoring(+line.DimForce, line, this.materialFactors, line.IsAccident);
      this.calculateUtilizationFactor(line.Components, line);
    }
  }

  protected async calculateUtilizationFactor(component: MooringComponent[], line: MooringAnchorLine) {
    const calculateUtilizationFactorDtos: CalculateUtilizationFactorDto[] = [];
    component.forEach((component) => {
      calculateUtilizationFactorDtos.push({
        Id: component.Id,
        HasKnot: component.HasKnot,
        MblRopes: line.MblRopes,
        MblSinkRopes: line.MblSinkRopes,
        MblStraps: line.MblStraps,
        MblChainShackleConnectionPlates: line.MblChainShackleConnectionPlates,
        MblUsedChainRopeWithKnot: line.MblUsedChainRopeWithKnot,
        MblAnchoring: line.MblAnchoring,
      });
    });

    const response = await this.mooringService.calculateUtilizationFactor({
      mooringId: this.mooringId,
      mooringStep: MooringStep.ANCHOR_LINE,
      dto: calculateUtilizationFactorDtos,
    });

    response?.data.forEach((element) => {
      component.find((x) => x.Id === element.Id).UtilizationFactor = element.UtilizationFactor;
    });
  }

  protected setComponentQuantity(component: MooringComponent, line: MooringAnchorLine) {
    if (component.CategoryName === 'Tau' || component.CategoryName === 'Rope') {
      component.Quantity = line.LengthOfRope;
    }
    if (component.CategoryName === 'Kjetting' || component.CategoryName === 'Chain') {
      component.Quantity = line.LengthOfBottomRope;
    }
  }

  protected setComponentsQuantity(components: MooringComponent[], line: MooringAnchorLine) {
    components.forEach((compponent) => {
      if (compponent.CategoryName === 'Tau' || compponent.CategoryName === 'Rope') {
        compponent.Quantity = line.LengthOfRope;
      }
      if (compponent.CategoryName === 'Kjetting' || compponent.CategoryName === 'Chain') {
        compponent.Quantity = line.LengthOfBottomRope;
      }
    });
  }

  protected override postPasteAdjustment(index: number, name: (typeof tableColumns)[number]['field']) {
    this.onInputChange(index, name);
  }

  protected unbind() {
    console.log('Unbinding AnchorLinesTable, unsubscribing from context and pubsub');
    this.pubsub.unsub();
    //wer
    super.unbind();
  }

  protected detached() {
    console.log('Detaching AnchorLinesTable, unsubscribing from context and pubsub');
    this.pubsub.unsub();
  }
}

const fieldsToReset: (keyof MooringAnchorLine)[] = [
  'DimForce',
  'IsAccident',
  'MblRopes',
  'MblSinkRopes',
  'MblStraps',
  'MblChainShackleConnectionPlates',
  'MblUsedChainRopeWithKnot',
  'MblAnchoring',
  'BottomAnchoringUplift',
  'DepthAtAnchoringPoint',
  'HoldingPowerAnchoring',
  'LengthOfBottomRope',
  'LengthOfRope',
  'TotalLength',
  'Slope',
  'StopPositionLat',
  'StopPositionLong',
];

const tableColumns: ListFieldTyped<MooringAnchorLine>[] = [
  {
    field: 'AnalysisNo',
    title: 'mooring.analysisNo',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    inputType: 'text',
  },
  {
    field: 'BuoyNo',
    title: 'mooring.buoy',
    selected: true,
    visible: true,
    filterType: 'TEXT',
  },
  {
    field: 'DimForce',
    title: 'mooring.dimForce',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'IsAccident',
    title: 'mooring.isAccident',
    helpText: 'mooring.dimForceDescription',
    selected: true,
    visible: true,
    filterType: 'BOOL',
    transform: toChecked,
    inputType: 'bool',
  },
  {
    field: 'MblRopes',
    title: 'mooring.minMblRope',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblSinkRopes',
    title: 'mooring.minMblSinkRope',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblStraps',
    title: 'mooring.minMblStrap',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblChainShackleConnectionPlates',
    title: 'mooring.minMblChain',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblUsedChainRopeWithKnot',
    title: 'mooring.minMblUsedChainRopeWithKnot',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblAnchoring',
    title: 'mooring.minMblAnchoring',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'BottomAnchoringUplift',
    title: 'mooring.bottomAnchoringUplift',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'DepthAtAnchoringPoint',
    title: 'mooring.depthOfAnchoringPoint',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toM,
    inputType: 'float',
  },
  {
    field: 'HoldingPowerAnchoring',
    title: 'mooring.holdingPowerAnchoring',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'LengthOfBottomRope',
    title: 'mooring.lengthOfBottomChain',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toM,
    inputType: 'float',
  },
  {
    field: 'LengthOfRope',
    title: 'mooring.lengthOfRope',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toM,
    inputType: 'float',
  },
  {
    field: 'TotalLength',
    title: 'mooring.totalLengthByAnchoring',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toM,
    inputType: 'float',
  },
  {
    field: 'Slope',
    title: 'mooring.incline',
    helpText: 'mooring.slope_helpText',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    inputType: 'float',
  },
  {
    field: 'StopPositionLat',
    title: 'mooring.anchorLineStopN',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    inputType: 'gps',
  },
  {
    field: 'StopPositionLong',
    title: 'mooring.anchorLineStopE',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    inputType: 'gps',
  },
  {
    field: 'actions',
    title: '',
    selected: true,
    visible: true,
    filterType: 'none',
  },
];
